import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { along as turfAlong } from '@turf/along';
import { bbox as turfBbox } from '@turf/bbox';
import { booleanContains as turfBooleanContains } from '@turf/boolean-contains';
import { booleanCrosses as turfBooleanCrosses } from '@turf/boolean-crosses';
import { booleanPointInPolygon as turfBooleanPointInPolygon } from '@turf/boolean-point-in-polygon';
import { circle as turfCircle } from '@turf/circle';
import { distance as turfDistance } from '@turf/distance';
import { length as turfLength } from '@turf/length';
import { stringify } from 'qs';
import * as R from 'ramda';

import AssetGroupMarker from '@atom/components/common/map/markers/AssetGroupMarker';
import MapClusterMarker from '@atom/components/common/map/markers/ClusterMarker';
import WorkOrderGroupMarker from '@atom/components/common/map/markers/WorkOrderGroupMarker';
import UserThumbnail from '@atom/components/common/UserThumbnail';
import { Layers } from '@atom/components/mapPortal/hooks/layersHook';
import { Location } from '@atom/hooks/useCurrentLocation';
import { Icon } from '@atom/mui';
import { GeoJSONLineString, GeoJSONPoint } from '@atom/types/geojson';
import { GoogleMapsLocation } from '@atom/types/gmapsLocation';
import {
  FormatInputEntity,
  FormatMapDataSharedProps,
  FormattedMapData,
  FormattedMarker,
  MapCenterAndZoomParams,
  MapEditMetadata,
  MapEditMetadataType,
  MapParams,
  MapShape,
  MapShapeType,
} from '@atom/types/map';
import { Preferences } from '@atom/types/preferences';
import { MAP_ENDPOINT } from '@atom/utilities/endpoints';
import markerUtilities from '@atom/utilities/markerUtilities';

import { getColorFromColorId } from './colorUtilities';
import schemaUtilities from './schemaUtilities';
import { getWorkOrderStatusColor } from './workOrderStatusUtilities';

// Center of United States
export const DEFAULT_MAP_CENTER = { lat: 39.8283, lng: -98.5795 };
// Zoom to show entire United States
export const DEFAULT_MAP_ZOOM = 5;
// Resolution order:
//  1. window.__APP_CONFIG__.GOOGLE_MAPS_API_KEY — injected at container start
//     from Secret Manager via /config.js (deployed prototype; see
//     config.js.template + proto.yaml `secrets:`).
//  2. import.meta.env.VITE_GOOGLE_MAPS_API_KEY — build-time, from .env.local
//     for local dev.
//  3. The hardcoded production key — HTTP-referer restricted (blocks localhost
//     and the sandbox host), so it's a last resort only.
const runtimeMapsKey = (
  window as unknown as { __APP_CONFIG__?: { GOOGLE_MAPS_API_KEY?: string } }
).__APP_CONFIG__?.GOOGLE_MAPS_API_KEY?.trim();
const buildMapsKey = (
  import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined
)?.trim();
export const GOOGLE_MAPS_API_KEY =
  runtimeMapsKey || buildMapsKey || 'AIzaSyA6yrGnEFLHQhwjNqF2uFlsZi4pwqtRQDg';
export const GOOGLE_MAPS_LIBRARIES = ['places', 'geometry', 'drawing'];

export const zipCoordinatePair = R.zipObj(['lng', 'lat']);

export const unzipCoordinatePair = coordinates => [
  coordinates.lng || 0,
  coordinates.lat || 0,
];

export const getGeoJSONLocation = (location: { lat: number; lng: number }) => {
  return {
    type: 'Point' as const,
    coordinates: [
      R.pathOr(0, ['lng'])(location),
      R.pathOr(0, ['lat'])(location),
    ],
  };
};

const COORDINATE_REGEX =
  /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;

export const parseCoordinates = (text: string): GoogleMapsLocation | null => {
  if (!COORDINATE_REGEX.test(text)) {
    return null;
  }
  const [latStr, lngStr] = text.split(',');
  return { lat: parseFloat(latStr), lng: parseFloat(lngStr) };
};

export const generatePath = coordinates => coordinates.map(zipCoordinatePair);

export const getLineStringCenter = (shape: {
  type: 'LineString';
  coordinates: number[][];
}) => {
  const line = {
    type: 'Feature' as const,
    properties: {},
    geometry: shape,
  };

  const lengthToCenter = turfLength(line) / 2;
  const center = turfAlong(line, lengthToCenter);

  return center;
};

export const getGoogleShapeCenter = shape => {
  if (R.isNil(shape) || R.isEmpty(shape)) {
    // Return a generic center
    return { lng: 0, lat: 0 };
  }

  if (shape.type === 'Point') {
    const coordinates = zipCoordinatePair(shape.coordinates);
    return {
      lat: Number(coordinates?.lat),
      lng: Number(coordinates?.lng),
    };
  }

  if (shape.type === 'LineString') {
    const coordinates = zipCoordinatePair(
      getLineStringCenter(shape).geometry.coordinates,
    );
    return {
      lat: Number(coordinates?.lat),
      lng: Number(coordinates?.lng),
    };
  }

  // Return a generic center
  return { lng: 0, lat: 0 };
};

export const getFitBoundsFromShape = shape => {
  if (R.isNil(shape) || R.isEmpty(shape)) {
    return null;
  }

  if (shape.type === 'Point') {
    return null;
  }

  if (shape.type === 'LineString') {
    return turfBbox(shape);
  }

  return null;
};

export const getGeoJsonFromMapBounds = map => {
  try {
    const bounds = map.getBounds().toJSON();
    const northwest = [bounds.west, bounds.north];
    const northeast = [bounds.east, bounds.north];
    const southeast = [bounds.east, bounds.south];
    const southwest = [bounds.west, bounds.south];
    return {
      type: 'Polygon',
      coordinates: [[northeast, northwest, southwest, southeast, northeast]],
    };
  } catch (error) {
    throw error;
  }
};

export const urlValueToLatLng = (
  urlValue: string,
): google.maps.LatLngLiteral => {
  const [lat, lng] = urlValue.split(',').map(Number);
  return { lat, lng };
};

export const getStaticImage = (entityLocation, size = '340x160') => {
  const center = getGoogleShapeCenter(entityLocation);

  const zoom = 18;
  const maptype = 'satellite';
  const baseEndpoint = 'https://maps.googleapis.com/maps/api/staticmap';

  const url = `${baseEndpoint}?center=${center.lat},${center.lng}&zoom=${zoom}&size=${size}&maptype=${maptype}&format=png&visual_refresh=true&key=${GOOGLE_MAPS_API_KEY}`;

  return url;
};

// getMapCenterAndZoomParams sets the initial map params if they do not exist
// or simply passes through the current map params

// Priority for setting map center is as follows:
// 1. User's current location
// 2. Map center from tenant preferences
// 3. Default center of United States

// Priority for setting zoom level is as follows:
// 1. Zoom level from tenant preferences
// 2. Default zoom level of 5
export const getMapCenterAndZoomParams = (
  map: google.maps.Map,
  queryParams: MapCenterAndZoomParams | MapParams,
  preferences: Preferences,
  currentUserLocation: GoogleMapsLocation,
): MapCenterAndZoomParams => {
  const preferencesZoom = preferences?.map?.zoom;

  if (!queryParams.center || !queryParams.zoom) {
    return {
      zoom: preferencesZoom || DEFAULT_MAP_ZOOM,
      center: `${currentUserLocation.lat},${currentUserLocation.lng}`,
    };
  }

  return {
    ...queryParams,
    zoom: map.getZoom(),
    center: map.getCenter().toUrlValue(),
  };
};

const getMapEditMetadataIcon = (
  data: any,
  type: MapEditMetadataType,
): React.ReactNode => {
  switch (type) {
    case MapEditMetadataType.ASSET:
      return schemaUtilities.getSchemaIconFromSchemaOrAsset(data);
    case MapEditMetadataType.WORK_ORDER:
      return <Icon style={{ height: '34px', width: '34px' }}>work</Icon>;
    case MapEditMetadataType.USER:
      return <UserThumbnail image={data.photoUrl} alt={data.name} />;
    default:
      return <div />;
  }
};

const getMapEditMetadataName = (
  data: any,
  type: MapEditMetadataType,
): string => {
  switch (type) {
    case MapEditMetadataType.ASSET:
      return data?.name;
    case MapEditMetadataType.WORK_ORDER:
      return data?.name;
    case MapEditMetadataType.USER:
      return `${data.firstName} ${data.lastName}`;
    default:
      return '';
  }
};

export const getMapEditMetadata = (
  data: any,
  canEdit: boolean,
  type: MapEditMetadataType,
): MapEditMetadata => {
  return {
    id: data?.id,
    type,
    icon: getMapEditMetadataIcon(data, type),
    name: getMapEditMetadataName(data, type),
    address: data?.address,
    location: data?.location,
    markerId: data?.markerId,
    statusId: data?.statusId,
    canEdit,
  };
};

export const getGeoJSONFromGoogleMapShape = (
  shape: MapShape,
): GeoJSONPoint | GeoJSONLineString => {
  switch (shape?.type) {
    case MapShapeType.MARKER: {
      const position = shape.getPosition();
      return {
        type: 'Point',
        coordinates: [position.lng(), position.lat()],
      };
    }
    case MapShapeType.POLYLINE: {
      const path = shape.getPath();
      return {
        type: 'LineString',
        coordinates: path
          .getArray()
          .map(item => [item.lng(), item.lat()] as [number, number]),
      };
    }
    default:
      return {
        type: 'Point',
        coordinates: [0, 0],
      };
  }
};

export const trimPointLatLng = (
  location: Location,
  decimalPlaces: number,
): Location => {
  return {
    ...location,
    coordinates: location.coordinates.map(item => {
      return location.type === 'LineString'
        ? item.map(subItem => subItem.toFixed(decimalPlaces))
        : item.toFixed(decimalPlaces);
    }),
  };
};

export const buildMapDataRequest = (layers: Layers, urlParams) => {
  const hasAllRequiredParams =
    urlParams.zoom && layers.assetSchemas && urlParams.coordinates;

  if (!hasAllRequiredParams) {
    return null;
  }

  const qsString = stringify(
    {
      zoomLevel: Number(urlParams.zoom) > 20 ? '20' : urlParams.zoom,
      coordinates: urlParams.coordinates,
      includeUsers: layers.users,
      includeWorkOrders: Boolean(
        layers.work &&
        (layers.workStatusIds.size ||
          layers.workTemplateIds.size ||
          layers.workTemplateFolderIds.size),
      ),
      includeAssets: Boolean(layers.assets && layers.assetSchemas.size),
      schemaIds: Array.from(layers.assetSchemas),
      statusIds: Array.from(layers.workStatusIds),
      workTemplateIds: Array.from(layers.workTemplateIds),
      workTemplateFolderIds: Array.from(layers.workTemplateFolderIds),
      clusterDensity: layers.clusterDensity,
    },
    {
      arrayFormat: 'comma',
      encode: false,
      sort: (item, next) => item?.localeCompare(next),
    },
  );

  return `${MAP_ENDPOINT}?${qsString}`;
};

const locationKey = (location: unknown): string =>
  JSON.stringify(
    (location as { coordinates?: unknown } | null)?.coordinates ?? null,
  );

const partitionByLocation = <T extends { location?: unknown }>(
  items: T[],
): { singles: T[]; groups: T[][] } => {
  const buckets = R.values(
    R.groupBy(item => locationKey(item.location), items),
  );

  const [groupBuckets, singleBuckets] = R.partition(
    bucket => bucket.length > 1,
    buckets,
  );

  return {
    singles: singleBuckets.map(bucket => bucket[0]),
    groups: groupBuckets,
  };
};

const encodeSvg = (element: React.ReactElement): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    ReactDOMServer.renderToStaticMarkup(element),
  )}`;

const clusterIconUrlCache = new Map<number, string>();

const getClusterIconUrl = (count: number): string => {
  const cached = clusterIconUrlCache.get(count);

  if (cached) {
    return cached;
  }

  const url = encodeSvg(<MapClusterMarker count={count} />);
  clusterIconUrlCache.set(count, url);

  return url;
};

const clusterAnchor = (count: number) =>
  count > 100 ? { x: 45, y: 45 } : { x: 35, y: 35 };

const getClusterIcon = (cluster: FormatInputEntity) => ({
  url: getClusterIconUrl(cluster.count),
  anchor: clusterAnchor(cluster.count),
});

const groupIdOf = (items: { id: string }[]): string =>
  items.map(item => item.id).join(',');

const getDistanceBetweenMarkers = (
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
): number => turfDistance(getGeoJSONLocation(from), getGeoJSONLocation(to));

export const getCurrentUserLocation = (): Promise<{
  lat: number;
  lng: number;
} | null> =>
  new Promise(resolve => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        const lat = R.pathOr(0, ['coords', 'latitude'])(position);
        const lng = R.pathOr(0, ['coords', 'longitude'])(position);
        resolve({ lat, lng });
      },
      () => resolve(null),
    );
  });

const CURSOR_RADIUS = 40;

export const getGrabShape =
  (map: google.maps.Map, mapData) => (event: google.maps.MapMouseEvent) => {
    const clickPosition = map.getProjection().fromLatLngToPoint(event.latLng);
    const clickJSON = event.latLng.toJSON();

    const pixelSize = Math.pow(2, -map.getZoom());

    const radiusPosition = clickPosition.x - CURSOR_RADIUS * pixelSize;
    const radiusPoint = new google.maps.Point(radiusPosition, clickPosition.y);
    const radiusLatLng = map.getProjection().fromPointToLatLng(radiusPoint);

    const mileRadius = getDistanceBetweenMarkers(
      clickJSON,
      radiusLatLng.toJSON(),
    );

    const circle = turfCircle(
      [clickJSON.lng, clickJSON.lat] as [number, number],
      mileRadius,
    );

    const { assets, workOrders, users } = mapData;
    const grabbed = [...assets, ...workOrders, ...users].filter(item => {
      const { location } = item;
      const { type, coordinates } = location;
      return type === 'Point'
        ? turfBooleanPointInPolygon(coordinates, circle)
        : turfBooleanCrosses(circle, location) ||
            turfBooleanContains(circle, location);
    });
    const grabbedIds = grabbed.map(({ id }) => id).join(',');

    return grabbedIds;
  };

const haveSameIds = (idsString: string, itemIdString: string): boolean => {
  if (!idsString || !itemIdString) {
    return false;
  }

  const ids = idsString.split(',');
  const itemIds = itemIdString.split(',');

  return ids.some(id => itemIds.includes(id));
};

const lineStringPath = (location: {
  type?: string;
  coordinates?: unknown;
}): { lat: number; lng: number }[] | null =>
  location?.type === 'LineString'
    ? generatePath(location.coordinates as [number, number][])
    : null;

const formatAsset = (
  asset: FormatInputEntity,
  sharedProps: FormatMapDataSharedProps,
  activeIds: string,
  disabledIds: string,
): FormattedMarker => {
  const isActive = haveSameIds(activeIds, asset.id);
  const disabled = haveSameIds(disabledIds, asset.id);

  return {
    id: asset.id,
    position: getGoogleShapeCenter(asset.location),
    path: lineStringPath(asset.location),
    icon: {
      url: isActive
        ? markerUtilities.getActiveAssetIconFromSchemaMarkerId(asset.markerId)
        : markerUtilities.getInactiveAssetIconFromSchemaMarkerId(
            asset.markerId,
          ),
      anchor: isActive ? { x: 20, y: 36 } : { x: 17, y: 17 },
    },
    color: getColorFromColorId(asset.markerId),
    isActive,
    disabled,
    clickable: sharedProps.clickable,
  };
};

const formatAssetGroup = (
  group: FormatInputEntity[],
  sharedProps: FormatMapDataSharedProps,
  activeIds: string,
  disabledIds: string,
): FormattedMarker => {
  const id = groupIdOf(group);
  const isActive = haveSameIds(activeIds, id);
  const disabled = haveSameIds(disabledIds, id);
  const head = group[0];
  const color = getColorFromColorId(head.markerId);

  return {
    id,
    position: getGoogleShapeCenter(head.location),
    path: lineStringPath(head.location),
    icon: {
      url: encodeSvg(
        <AssetGroupMarker
          count={group.length}
          color={color}
          isActive={isActive}
        />,
      ),
      anchor: isActive ? { x: 20, y: 36 } : { x: 17, y: 17 },
    },
    color,
    isActive,
    disabled,
    clickable: sharedProps.clickable,
  };
};

const formatWorkOrder = (
  workOrder: FormatInputEntity,
  sharedProps: FormatMapDataSharedProps,
  activeIds: string,
  disabledIds: string,
): FormattedMarker => {
  const isActive = haveSameIds(activeIds, workOrder.id);
  const disabled = haveSameIds(disabledIds, workOrder.id);

  return {
    id: workOrder.id,
    position: getGoogleShapeCenter(workOrder.location),
    icon: {
      url: markerUtilities.getWorkOrderSvg(workOrder.statusId, isActive),
      anchor: { x: 20, y: 32 },
    },
    size: { x: 18, y: 40 },
    isActive,
    disabled,
    clickable: sharedProps.clickable,
    zIndex: isActive ? 504 : 500,
  };
};

const formatWorkOrderGroup = (
  group: FormatInputEntity[],
  sharedProps: FormatMapDataSharedProps,
  activeIds: string,
  disabledIds: string,
): FormattedMarker => {
  const id = groupIdOf(group);
  const isActive = haveSameIds(activeIds, id);
  const disabled = haveSameIds(disabledIds, id);

  return {
    id,
    position: getGoogleShapeCenter(group[0].location),
    icon: {
      url: encodeSvg(
        <WorkOrderGroupMarker count={group.length} isActive={isActive} />,
      ),
      anchor: { x: 25, y: 32 },
    },
    isActive,
    disabled,
    clickable: sharedProps.clickable,
  };
};

const formatUser = (
  user: FormatInputEntity,
  sharedProps: FormatMapDataSharedProps,
  activeIds: string,
  disabledIds: string,
): FormattedMarker => {
  const isActive = haveSameIds(activeIds, user.id);
  const disabled = haveSameIds(disabledIds, user.id);

  return {
    id: user.id,
    position: getGoogleShapeCenter(user.location),
    icon: {
      url: markerUtilities.getUserSvg(isActive),
      anchor: { x: 14, y: 34 },
    },
    isActive,
    disabled,
    clickable: sharedProps.clickable,
    zIndex: isActive ? 505 : 501,
  };
};

const formatCluster = (cluster: FormatInputEntity): FormattedMarker => ({
  id: cluster.id,
  position: getGoogleShapeCenter(cluster.location),
  icon: getClusterIcon(cluster),
});

const formatTaskLocation = (
  location: FormatInputEntity,
  sharedProps: FormatMapDataSharedProps,
): FormattedMarker => {
  const isActive = location.isActive === true;

  return {
    id: location.id,
    position: getGoogleShapeCenter(location.geometry),
    path: lineStringPath(location.geometry),
    icon: {
      url: markerUtilities.getWorkOrderSvg(location.statusId, isActive),
      anchor: { x: 20, y: 32 },
    },
    color: getWorkOrderStatusColor(location.statusId),
    isActive: !!location.isActive,
    clickable: sharedProps.clickable,
  };
};

type MapDataInput = {
  assets?: FormatInputEntity[];
  workOrders?: FormatInputEntity[];
  users?: FormatInputEntity[];
  clusters?: FormatInputEntity[];
  locations?: FormatInputEntity[];
};

export const formatMapData = (
  sharedProps: FormatMapDataSharedProps,
  rawData: unknown,
): FormattedMapData => {
  const data = (rawData ?? {}) as MapDataInput;
  const activeIds = sharedProps.hoverId || sharedProps.activeIds || '';
  const disabledIds = sharedProps.disabledIds || '';

  const assetPartition = partitionByLocation(data.assets ?? []);
  const workOrderPartition = partitionByLocation(data.workOrders ?? []);

  return {
    assets: assetPartition.singles.map(asset =>
      formatAsset(asset, sharedProps, activeIds, disabledIds),
    ),
    assetGroups: assetPartition.groups.map(group =>
      formatAssetGroup(group, sharedProps, activeIds, disabledIds),
    ),
    workOrders: workOrderPartition.singles.map(workOrder =>
      formatWorkOrder(workOrder, sharedProps, activeIds, disabledIds),
    ),
    workOrderGroups: workOrderPartition.groups.map(group =>
      formatWorkOrderGroup(group, sharedProps, activeIds, disabledIds),
    ),
    users: (data.users ?? []).map(user =>
      formatUser(user, sharedProps, activeIds, disabledIds),
    ),
    clusters: (data.clusters ?? []).map(formatCluster),
    locations: (data.locations ?? []).map(location =>
      formatTaskLocation(location, sharedProps),
    ),
  };
};
